/**
 * Home / Dashboard (U2) — a raiz do app: painel da competência corrente com o
 * estado, o stepper do ciclo, as pendências do mês (divergências, buracos de
 * vigência futura, ajustes de ponto, crédito de retenção) e o botão da próxima
 * ação óbvia. Dados: GET /competencias/:id/painel (camada de serviço).
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Group, SimpleGrid, Stepper, Text, Title } from "@mantine/core";
import { IconAlertTriangle, IconArrowRight, IconCalendarCheck, IconCalendarTime, IconReceiptTax, IconUserEdit } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia, fmtData, fmtHhMm, fmtMoeda, ROTULO_ESTADO } from "../format";
import { Carregando, Vazio } from "../componentes/Estados";
import { navegar } from "../roteador";

interface Painel {
  competencia: { id: number; estado: string; tipoCalculo: string };
  lancamentos: number;
  importacoesPonto: number;
  runs: { total: number; ultima: { id: string; executadoEm: string; motorVersion: string; tipoCalculo: string } | null };
  artefatos: number;
  retornos: { total: number; ultimaEmissao: string | null };
  divergencias: { bloqueantesAbertas: number; alertasAbertas: number; justificadas: number };
  ajustesPontoRecentes: { funcionarioNome: string; data: string; horaMinutos: number; justificativa: string | null; status: string; aprovadoPor: string | null }[];
  retencao: { creditoMesCentavos: number; acumuladoCentavos: number };
  vigenciaFutura: { competencia: number; problemas: string[] };
  /** M4.6 — calendário de obrigações do mês (agenda orientativa). */
  obrigacoes: { dataIso: string; tipo: string; titulo: string; referencia: string; ajustadoDiaUtil: boolean }[];
}

const PASSOS = ["Ponto", "Variáveis", "Cálculo", "Arquivos", "Retorno", "Divergências", "Fechar"];

/** Passo atual do ciclo (derivado do estado real — guia, não trava). */
function passoAtual(p: Painel): number {
  if (p.competencia.estado === "fechada") return PASSOS.length;
  if (p.competencia.estado === "retificada") return 2;
  if (p.retornos.total > 0) return p.competencia.estado === "conferida" ? 6 : 5;
  if (p.runs.total > 0) return 3;
  if (p.lancamentos > 0 || p.importacoesPonto > 0) return 1;
  return 0;
}

/** Próxima ação óbvia (derivada do painel). */
function proximaAcao(p: Painel): { texto: string; destino: string } {
  const d = `/competencias/${p.competencia.id}`;
  switch (p.competencia.estado) {
    case "aberta":
      return p.runs.total === 0
        ? { texto: "Importar o ponto e lançar as variáveis", destino: d }
        : { texto: "Revisar e calcular", destino: d };
    case "em_calculo":
      if (p.retornos.total === 0) return { texto: "Gerar arquivos e importar o retorno", destino: d };
      if (p.divergencias.bloqueantesAbertas > 0) return { texto: `Resolver ${p.divergencias.bloqueantesAbertas} divergência(s) bloqueante(s)`, destino: d };
      return { texto: "Conferir e fechar", destino: d };
    case "conferida":
      return { texto: "Fechar a competência", destino: d };
    case "fechada":
      return { texto: "Ver snapshot da competência fechada", destino: d };
    default:
      return { texto: "Retificar", destino: d };
  }
}

export function HomePage() {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    (async () => {
      try {
        const comps = await api.get<{ competencias: { id: number; estado: string }[] }>("/competencias");
        const atual = comps.competencias.find((c) => c.estado !== "fechada") ?? comps.competencias[comps.competencias.length - 1];
        if (!atual) {
          setPainel(null);
          return;
        }
        setPainel(await api.get<Painel>(`/competencias/${atual.id}/painel`));
      } catch (e) {
        setErro(e);
      }
    })();
  }, []);

  if (erro) {
    return <Alert color="red" title="Falha ao carregar o painel">{String(erro)}</Alert>;
  }
  if (!painel) {
    return (
      <>
        <Title order={2} mb="md">Folha de pagamento</Title>
        <Carregando linhas={5} />
      </>
    );
  }

  const acao = proximaAcao(painel);
  const pendencias = painel.divergencias.bloqueantesAbertas + painel.divergencias.alertasAbertas + painel.vigenciaFutura.problemas.length;

  return (
    <>
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Folha de pagamento</Title>
        <Button variant="default" component="a" href="#/competencias" rightSection={<IconArrowRight size={14} />}>
          Todas as competências
        </Button>
      </Group>

      <Card withBorder shadow="xs" mb="md" style={{ borderLeft: "6px solid var(--mantine-color-sillis-7)" }}>
        <Group justify="space-between" wrap="wrap" mb="sm">
          <Group gap="sm">
            <Text fw={700} size="xl">
              {fmtCompetencia(painel.competencia.id)}
            </Text>
            <Badge size="lg">{ROTULO_ESTADO[painel.competencia.estado] ?? painel.competencia.estado}</Badge>
            {/* 03/09/2026 (pedido do dono): a versão do motor NÃO aparece na Home —
                "não importa para o usuário" (detalhe técnico, segue na página da competência) */}
          </Group>
          <Button component="a" href={`#${acao.destino}`} rightSection={<IconArrowRight size={16} />}>
            {acao.texto}
          </Button>
        </Group>
        <Stepper size="sm" active={passoAtual(painel)} onStepClick={() => navegar(acao.destino)}>
          {PASSOS.map((label) => (
            <Stepper.Step key={label} label={label} />
          ))}
        </Stepper>
      </Card>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
        <Card withBorder shadow="xs">
          <Group gap="sm" mb="xs">
            <IconAlertTriangle size={18} color="var(--mantine-color-red-7)" />
            <Text fw={700}>Divergências da conferência</Text>
          </Group>
          {painel.retornos.total === 0 ? (
            <Text size="sm" c="dimmed">Sem retorno importado ainda — as divergências aparecem depois da importação.</Text>
          ) : painel.divergencias.bloqueantesAbertas + painel.divergencias.alertasAbertas === 0 ? (
            <Text size="sm" c="sillis.7">Nenhuma divergência aberta — {painel.divergencias.justificadas} justificada(s) com referência.</Text>
          ) : (
            <>
              <Text size="sm">
                <Text span fw={700} c="red.7">{painel.divergencias.bloqueantesAbertas}</Text> bloqueante(s) aberta(s) ·{" "}
                <Text span fw={700} c="yellow.8">{painel.divergencias.alertasAbertas}</Text> alerta(s) aberto(s) ·{" "}
                {painel.divergencias.justificadas} justificada(s)
              </Text>
              <Button size="xs" variant="light" mt="sm" component="a" href={`#${acao.destino}`}>
                Resolver agora
              </Button>
            </>
          )}
        </Card>

        <Card withBorder shadow="xs">
          <Group gap="sm" mb="xs">
            <IconCalendarCheck size={18} color="var(--mantine-color-sillis-7)" />
            <Text fw={700}>Vigências para {fmtCompetencia(painel.vigenciaFutura.competencia)}</Text>
          </Group>
          {painel.vigenciaFutura.problemas.length === 0 ? (
            <Text size="sm" c="sillis.7">Tudo coberto para o próximo mês — nenhum buraco de vigência.</Text>
          ) : (
            <>
              <Text size="sm" c="red.7" fw={600} mb={4}>
                {painel.vigenciaFutura.problemas.length} pendência(s) para o próximo mês:
              </Text>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {painel.vigenciaFutura.problemas.slice(0, 3).map((p, i) => (
                  <li key={i}>
                    <Text size="sm">{p}</Text>
                  </li>
                ))}
              </ul>
              <Button size="xs" variant="light" mt="sm" component="a" href="#/parametros">
                Acertar parâmetros
              </Button>
            </>
          )}
        </Card>

        <Card withBorder shadow="xs">
          <Group gap="sm" mb="xs">
            <IconUserEdit size={18} color="var(--mantine-color-sillis-7)" />
            <Text fw={700}>Ajustes de ponto recentes</Text>
          </Group>
          {painel.ajustesPontoRecentes.length === 0 ? (
            <Text size="sm" c="dimmed">Nenhum ajuste importado nesta competência.</Text>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {painel.ajustesPontoRecentes.map((a, i) => (
                <li key={i}>
                  <Text size="sm">
                    {a.funcionarioNome.split(" ")[0]} — {fmtData(a.data)} {fmtHhMm(a.horaMinutos)} ({a.justificativa ?? "ajuste"})
                    {a.aprovadoPor ? ` · ok ${a.aprovadoPor.split(" ")[0]}` : ""}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card withBorder shadow="xs">
          <Group gap="sm" mb="xs">
            <IconCalendarTime size={18} color="var(--mantine-color-sillis-7)" />
            <Text fw={700}>Obrigações do mês</Text>
          </Group>
          {(painel.obrigacoes ?? []).length === 0 ? (
            <Text size="sm" c="dimmed">Nenhuma obrigação com vencimento neste mês.</Text>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {painel.obrigacoes.map((o, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  <Text size="sm">
                    <Text span fw={700}>{fmtData(o.dataIso)}</Text> — {o.titulo}
                    {o.ajustadoDiaUtil ? " (antecipado: dia 20 não é útil)" : ""}
                  </Text>
                  <Text size="xs" c="dimmed">{o.referencia}</Text>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card withBorder shadow="xs">
          <Group gap="sm" mb="xs">
            <IconReceiptTax size={18} color="var(--mantine-color-sillis-7)" />
            <Text fw={700}>Retenção de INSS s/ NF</Text>
          </Group>
          <Text size="sm">
            Crédito do mês: <strong>{fmtMoeda(painel.retencao.creditoMesCentavos)}</strong>
            <br />
            Acumulado para compensar: <strong>{fmtMoeda(painel.retencao.acumuladoCentavos)}</strong>
          </Text>
          <Button size="xs" variant="light" mt="sm" component="a" href="#/tomadores">
            Ver NFs e compensação
          </Button>
        </Card>
      </SimpleGrid>

      {pendencias === 0 && painel.competencia.estado === "fechada" && (
        <Vazio texto="Competência fechada e sem pendências — abra a próxima quando chegar a hora." acao="Abrir próxima competência" onAcao={() => navegar("/competencias")} />
      )}
    </>
  );
}
